import { useEffect, useRef, useState } from "react";
import { Music2, Pause, Play, Loader2, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";

const srcFor = (s) => {
  if (s.provider === "youtube") return `https://www.youtube.com/embed/${s.id}?autoplay=1&loop=1&playlist=${s.id}&enablejsapi=1&controls=0&playsinline=1`;
  if (s.provider === "spotify") return `https://open.spotify.com/embed/${s.id}?autoplay=1`;
  return `https://w.soundcloud.com/player/?url=${encodeURIComponent(s.url)}&auto_play=true&visual=false`;
};

const send = (frame, s, play) => {
  const w = frame?.contentWindow;
  if (!w) return;
  if (s.provider === "youtube") w.postMessage(JSON.stringify({ event: "command", func: play ? "playVideo" : "pauseVideo", args: "" }), "*");
  else if (s.provider === "soundcloud") w.postMessage(JSON.stringify({ method: play ? "play" : "pause" }), "https://w.soundcloud.com");
  else w.postMessage({ command: play ? "resume" : "pause" }, "*");
};

// Hidden background player; tries autoplay, then starts on the visitor's first tap.
export const ProfileSongPlayer = ({ song }) => {
  const frame = useRef(null);
  const [playing, setPlaying] = useState(true);
  useEffect(() => {
    setPlaying(true);
    const kick = () => send(frame.current, song, true);
    const t = setTimeout(kick, 1500);
    window.addEventListener("pointerdown", kick, { once: true });
    window.addEventListener("keydown", kick, { once: true });
    return () => { clearTimeout(t); window.removeEventListener("pointerdown", kick); window.removeEventListener("keydown", kick); };
  }, [song]);
  const toggle = (e) => { e.stopPropagation(); send(frame.current, song, !playing); setPlaying(!playing); };
  return (
    <>
      <iframe ref={frame} title="Profile song" src={srcFor(song)} allow="autoplay; encrypted-media" data-testid="profile-song-frame"
        style={{ position: "fixed", width: 1, height: 1, left: -10, bottom: -10, opacity: 0, pointerEvents: "none", border: 0 }} />
      <button data-testid="profile-song-toggle" onClick={toggle} onPointerDown={(e) => e.stopPropagation()}
        className="fixed bottom-24 md:bottom-6 left-4 z-[55] flex items-center gap-2 max-w-[60vw] bg-black/70 backdrop-blur-md border border-[rgb(var(--kv-glow)/0.4)] text-white text-xs pl-2 pr-3 py-1.5 rounded-full shadow-[0_0_16px_-4px_rgb(var(--kv-glow)/0.6)]">
        <span className="grid place-items-center w-6 h-6 rounded-full bg-[rgb(var(--kv-glow))] text-black shrink-0">{playing ? <Pause size={12} /> : <Play size={12} />}</span>
        <Music2 size={12} className={`shrink-0 ${playing ? "animate-pulse text-[rgb(var(--kv-glow))]" : "text-[#71717A]"}`} />
        <span className="truncate">{song.title || "Profile song"}</span>
      </button>
    </>
  );
};

export const ProfileSongEditor = ({ song, onChange }) => {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState(song?.url || "");
  const [title, setTitle] = useState(song?.title || "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/auth/profile-song", { url, title });
      onChange(data.profile_song);
      toast.success("Profile song saved");
      setOpen(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not save"); }
    setBusy(false);
  };
  const remove = async () => {
    setBusy(true);
    try { await api.delete("/auth/profile-song"); onChange(null); setUrl(""); setTitle(""); toast.success("Profile song removed"); setOpen(false); }
    catch { toast.error("Could not remove"); }
    setBusy(false);
  };
  if (!open) {
    return (
      <button data-testid="profile-song-edit-btn" onClick={() => setOpen(true)} className="flex items-center gap-2 border border-white/10 text-[#9CA3AF] font-semibold text-sm px-4 py-2.5 rounded-lg hover:text-white transition-colors">
        <Music2 size={14} /> {song ? "Change song" : "Add song"}
      </button>
    );
  }
  return (
    <div data-testid="profile-song-editor" className="w-full max-w-md rounded-xl border border-white/10 bg-[#0F1014] p-3 space-y-2">
      <div className="flex items-center justify-between">
        <p className="text-white text-sm font-semibold flex items-center gap-2"><Music2 size={14} /> Profile song</p>
        <button onClick={() => setOpen(false)} className="text-[#71717A] hover:text-white"><X size={15} /></button>
      </div>
      <input data-testid="profile-song-url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="Spotify, YouTube or SoundCloud link"
        className="w-full bg-[#161922] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[rgb(var(--kv-glow)/0.6)]" />
      <input data-testid="profile-song-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Song name (optional)"
        className="w-full bg-[#161922] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[rgb(var(--kv-glow)/0.6)]" />
      <div className="flex gap-2">
        <button data-testid="profile-song-save" onClick={save} disabled={busy || !url.trim()} className="flex items-center gap-1.5 bg-[#0066FF] text-white text-sm font-semibold px-4 py-2 rounded-lg disabled:opacity-50">
          {busy && <Loader2 size={13} className="animate-spin" />} Save
        </button>
        {song && <button data-testid="profile-song-remove" onClick={remove} disabled={busy} className="flex items-center gap-1.5 text-[#FF2A5F] text-sm px-3 py-2"><Trash2 size={13} /> Remove</button>}
      </div>
    </div>
  );
};
